'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType,
  ControlIds,
  type CalculatorConfig,
  DataTableInputMode,
  type ControlValues,
  type SingleColumnSelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { ZScoreCalculator } from './z-score-calculator';
import { calculateZScores } from '../../../lib/stats/z-score';

export interface ZScoreControlValues extends ControlValues {
  [ControlIds.Values]: string;
  [ControlIds.InputValue]: string;
}

export const zScoreControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Values,
          type: ControlType.SingleColumnSelect,
          label: 'Reference data column',
          required: true,
          numericOnly: true,
          defaultValue: 'scores'
        } as SingleColumnSelectControl,
        {
          id: ControlIds.InputValue,
          type: ControlType.Number,
          label: 'Value to find z-score for',
          required: true,
          defaultValue: '80'
        } as NumberControl
      ]
    }
  ]
};

export const zScoreCalculatorConfig: CalculatorConfig<CalculatorId.Z_SCORE> = {
  id: CalculatorId.Z_SCORE,
  type: CalculatorType.Graph,
  name: 'Z-Score Calculator',
  dataTableInputMode: DataTableInputMode.Required,
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'scores',
        name: 'Test Scores', 
        values: ['65', '70', '75', '80', '85', '90', '95', '85', '80', '75', '95', '100', '60', '75', '85'] 
      },
      {
        id: 'heights',
        name: 'Heights (cm)',
        values: ['165', '170', '175', '168', '172', '180', '167', '173', '178', '169', '171', '176', '174', '177', '170']
      },
      {
        id: 'weights',
        name: 'Weights (kg)',
        values: ['68', '72', '75', '69', '71', '73', '77', '70', '74', '76', '72', '75', '78', '71', '73']
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Values]: 'scores',
    [ControlIds.InputValue]: '80'
  },
  controls: zScoreControls,
  getInitialConfig: (data: TableDataJSON) => ({
    [ControlIds.Values]: data.columns[0]?.id || ''
  }),
  getVisualizationData: (params: { data: TableDataJSON; controls: ControlValues }) => {
    const columnId = params.controls[ControlIds.Values] as string;
    const inputValue = Number(params.controls[ControlIds.InputValue]);
    const values = params.data.columns
      .find(col => col.id === columnId)
      ?.values
      .map(v => Number(v))
      .filter(v => !isNaN(v));
    
    if (!values || values.length === 0 || isNaN(inputValue)) return null;
    return calculateZScores(values, inputValue);
  },
  render: ({ visualizationData }) => <ZScoreCalculator visualizationData={visualizationData} />
}; 